import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { formulaCells, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionNamen: Lesson = {
  id: "namen",
  title: "Benannte Bereiche",
  summary: "=Menge*Preis statt =B7*C7 – eine Formel, die sich ohne Nachschlagen lesen lässt.",
  durationMinutes: 11,
  learningGoals: [
    "Zellen und Bereiche benennen",
    "Den Unterschied zwischen mappenweitem und blattlokalem Namen kennen",
    "Namen im Namens-Manager pflegen",
  ],
  steps: [
    {
      id: "namen-vergeben",
      title: "Ein Name statt einer Adresse",
      explanation: [
        "=B7*(1+Einstellungen!B1) rechnet richtig und verrät nicht, was es rechnet. " +
          "=Nettobetrag*(1+Mehrwertsteuersatz) rechnet dasselbe und erklärt sich " +
          "selbst. Der Unterschied ist ein Name, den man einmal vergibt.",
        "Der schnellste Weg führt über das Namenfeld links neben der " +
          "Bearbeitungsleiste: Zelle markieren, in das Feld klicken, Namen eintippen, " +
          "Eingabetaste. Ab sofort lässt sich der Name überall in der Arbeitsmappe " +
          "statt der Adresse verwenden – und er ist automatisch absolut, wandert beim " +
          "Ausfüllen also nicht mit. Damit erübrigt sich für Parameterzellen das " +
          "Hantieren mit Dollarzeichen.",
        "Für die Namen gelten Regeln: keine Leerzeichen, nicht mit einer Ziffer " +
          "beginnen, und der Name darf nicht wie ein Zellbezug aussehen. „MwSt“ geht, " +
          "„MwSt Satz“ nicht – dort nimmt man einen Unterstrich. Groß- und " +
          "Kleinschreibung ist Excel egal, sie bleibt aber erhalten und hilft beim " +
          "Lesen.",
        "Ein Name kann auch auf einen Bereich zeigen statt auf eine Zelle: " +
          "=SUMME(Umsatzwerte) über einen benannten Bereich. Seit es Tabellen gibt, " +
          "ist das seltener nötig – eine Tabelle bringt ihre Spaltennamen ja schon " +
          "mit. Für einzelne Parameterzellen, die zu keiner Tabelle gehören, bleiben " +
          "Namen aber das richtige Werkzeug.",
      ],
      keyPoints: [
        "Namenfeld links neben der Bearbeitungsleiste: Name eintippen, Eingabetaste.",
        "Namen sind immer absolut – Dollarzeichen erübrigen sich.",
        "Keine Leerzeichen, nicht mit Ziffer beginnen, nicht wie ein Bezug aussehen.",
      ],
      task: {
        instruction:
          "Vergib für mindestens eine deiner Parameterzellen – etwa den Steuersatz " +
          "oder eine Schwelle – einen sprechenden Namen.",
        hint: "Zelle markieren, ins Namenfeld links neben der Bearbeitungsleiste klicken, Namen eintippen, Eingabetaste.",
        check: async () => {
          const mappe = await readWorkbook();

          if (mappe.names.length === 0) {
            return fail(
              "In der Arbeitsmappe ist noch kein Name vergeben.",
              "Markiere deine Parameterzelle, klicke ins Namenfeld links neben der Bearbeitungsleiste " +
                "und tippe einen Namen ein.",
            );
          }

          const erster = mappe.names[0];
          const beschreibung = [
            erster.rangeAddress !== null ? `zeigt auf ${erster.rangeAddress}` : null,
            erster.scope !== null ? `gilt für ${erster.scope === "Workbook" ? "die ganze Mappe" : `das Blatt „${erster.sheetName ?? "?"}“`}` : null,
          ]
            .filter((teil) => teil !== null)
            .join(", ");

          return pass(
            `${mappe.names.length} ${mappe.names.length === 1 ? "Name" : "Namen"} vergeben, ` +
              `zum Beispiel „${erster.name}“${beschreibung ? ` – ${beschreibung}` : ""}.`,
          );
        },
      },
    },
    {
      id: "namen-benutzen",
      title: "Den Namen auch benutzen",
      explanation: [
        "Ein Name allein bringt nichts – er muss in den Formeln ankommen. Bei neuen " +
          "Formeln geht das von selbst: Sobald du beim Schreiben die benannte Zelle " +
          "anklickst, setzt Excel den Namen statt der Adresse ein. Auch beim Tippen " +
          "schlägt die Autovervollständigung nach den ersten Buchstaben die " +
          "vorhandenen Namen vor.",
        "Für Formeln, die es schon gibt, gibt es einen Sammelbefehl: „Formeln“ → " +
          "„Namen definieren“ → „Namen übernehmen“. Excel durchsucht die markierten " +
          "Formeln nach Adressen, für die ein Name existiert, und ersetzt sie. Das " +
          "ist ein guter Moment, um zu sehen, wie viel lesbarer eine Tabelle dadurch " +
          "wird.",
        "Das Namenfeld ist außerdem eine Navigationshilfe: Klappt man es auf, stehen " +
          "alle Namen der Arbeitsmappe darin, und ein Klick springt zum jeweiligen " +
          "Bereich – auch über Blattgrenzen hinweg. Bei einer umfangreichen Datei ist " +
          "das oft der schnellste Weg an eine bestimmte Stelle.",
      ],
      keyPoints: [
        "Beim Schreiben die benannte Zelle anklicken – Excel setzt den Namen ein.",
        "Formeln → Namen definieren → Namen übernehmen ersetzt Adressen nachträglich.",
        "Das aufgeklappte Namenfeld springt zu jedem benannten Bereich.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass mindestens eine deiner Formeln einen benannten Bereich " +
          "benutzt statt einer Zelladresse.",
        hint: "Formel bearbeiten und die benannte Zelle anklicken – oder Formeln → Namen definieren → Namen übernehmen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob dort der Name statt der Zelladresse steht.",
              true,
            );
          }

          if (mappe.names.length === 0) {
            return fail(
              "In der Arbeitsmappe ist noch kein Name vergeben.",
              "Löse zuerst die Aufgabe im vorigen Schritt.",
            );
          }

          const namen = mappe.names.map((eintrag) => eintrag.name).filter((name) => name.length > 0);
          const formeln = formulaCells(mappe);

          // Der Name muss als eigenständiges Wort in der Formel stehen - nicht
          // als Teil eines längeren Namens und nicht in einem Textbaustein.
          const mitNamen = formeln.filter((zelle) =>
            namen.some((name) => {
              const ohneText = zelle.formula.replace(/"[^"]*"/g, '""');
              return new RegExp(
                `(^|[^A-Za-zÄÖÜäöüß0-9_.])${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![A-Za-zÄÖÜäöüß0-9_.[])`,
                "u",
              ).test(ohneText);
            }),
          );

          if (mitNamen.length === 0) {
            return fail(
              `Die Namen ${namen.slice(0, 3).map((name) => `„${name}“`).join(", ")} sind vergeben, ` +
                `aber keine der ${formeln.length} Formeln benutzt sie.`,
              "Bearbeite eine Formel und klicke die benannte Zelle an – Excel setzt dann den Namen ein. " +
                "Für alle vorhandenen auf einmal: Formeln → Namen definieren → Namen übernehmen.",
            );
          }

          const erste = mitNamen[0];
          const verwendet = namen.filter((name) => erste.formula.includes(name));
          return pass(
            `In ${erste.address} steht „${erste.formula}“ – die Formel benennt, womit sie rechnet` +
              (verwendet.length > 0 ? ` (${verwendet.map((name) => `„${name}“`).join(", ")})` : "") +
              ".",
          );
        },
      },
    },
    {
      id: "namens-manager",
      title: "Der Namens-Manager",
      explanation: [
        "Unter „Formeln“ → „Namens-Manager“ oder mit Strg + F3 öffnet sich die " +
          "Übersicht über alle Namen der Arbeitsmappe: Name, aktueller Wert, worauf " +
          "er zeigt, sein Gültigkeitsbereich und ein Kommentarfeld. Hier wird " +
          "geändert, gelöscht und aufgeräumt.",
        "Der Gültigkeitsbereich ist die wichtigste Spalte. Ein Name gilt entweder für " +
          "die ganze Arbeitsmappe – das ist die Voreinstellung – oder nur für ein " +
          "bestimmtes Blatt. Blattlokale Namen sind nützlich, wenn dieselbe Größe auf " +
          "mehreren Blättern vorkommt: Jedes Quartalsblatt hat sein eigenes " +
          "„Gesamtsumme“, und die Formeln auf dem Blatt greifen jeweils auf das " +
          "richtige zu.",
        "Sie sind aber auch eine Fehlerquelle, weil zwei Namen gleich heißen und " +
          "Verschiedenes bedeuten können. Wer nicht ausdrücklich einen blattlokalen " +
          "Namen braucht, lässt es bei der mappenweiten Voreinstellung.",
        "In gewachsenen Dateien lohnt sich ein Blick in den Manager immer. Dort " +
          "sammeln sich Namen mit dem Wert #BEZUG!, die auf gelöschte Bereiche " +
          "zeigen, und Namen, die ein Datenimport hinterlassen hat. Beides lässt sich " +
          "gefahrlos löschen, sofern keine Formel darauf verweist – der Filter " +
          "„Namen mit Fehlern“ oben rechts im Dialog findet die kaputten in einem " +
          "Schritt.",
      ],
      keyPoints: [
        "Strg + F3 öffnet den Namens-Manager.",
        "Mappenweit ist die Voreinstellung; blattlokal nur, wenn es wirklich nötig ist.",
        "Der Filter „Namen mit Fehlern“ findet Reste mit #BEZUG! in einem Schritt.",
      ],
      task: {
        instruction:
          "Sieh im Namens-Manager nach, ob es Namen mit Fehlern gibt, und räume sie " +
          "auf.",
        hint: "Strg + F3, dann oben rechts über „Filter“ die Auswahl „Namen mit Fehlern“.",
        check: async () => {
          const mappe = await readWorkbook();

          if (mappe.names.length === 0) {
            return fail(
              "In der Arbeitsmappe ist noch kein Name vergeben.",
              "Löse zuerst die Aufgaben der vorigen Schritte.",
            );
          }

          if (!mappe.features.nameDetails) {
            return nichtPruefbar(
              "Der Gültigkeitsbereich und die Formel eines Namens",
              "1.7",
              "Öffne mit Strg + F3 den Namens-Manager und filtere oben rechts auf „Namen mit Fehlern“.",
              true,
            );
          }

          const kaputt = mappe.names.filter(
            (eintrag) =>
              eintrag.type === "Error" ||
              (eintrag.formula !== null && /#(BEZUG|REF)!/i.test(eintrag.formula)),
          );

          if (kaputt.length > 0) {
            return fail(
              `${kaputt.length} ${kaputt.length === 1 ? "Name zeigt" : "Namen zeigen"} ins Leere: ` +
                `${kaputt.map((eintrag) => `„${eintrag.name}“`).join(", ")}. Der Bereich, auf den ` +
                "sie verwiesen, wurde gelöscht.",
              "Öffne mit Strg + F3 den Namens-Manager, markiere die betroffenen Einträge und lösche sie – " +
                "oder setze über „Bearbeiten“ einen gültigen Bezug ein.",
            );
          }

          const blattlokal = mappe.names.filter((eintrag) => eintrag.scope === "Worksheet");
          return pass(
            `Alle ${mappe.names.length} Namen zeigen auf gültige Bereiche.` +
              (blattlokal.length > 0
                ? ` ${blattlokal.length} davon ${blattlokal.length === 1 ? "gilt" : "gelten"} nur für ein ` +
                  `einzelnes Blatt (${blattlokal.map((eintrag) => `„${eintrag.name}“`).join(", ")}) – ` +
                  "prüfe kurz, ob das Absicht ist."
                : ""),
          );
        },
      },
    },
  ],
};
